import type { KeyboardEvent, ReactNode } from 'react'

export type TabItem = {
  key: string
  label: ReactNode
  /** 角标数量，>0 才显示 */
  count?: number
  /** 角标配色：danger 强调待办，neutral 信息数 */
  countTone?: 'danger' | 'neutral'
}

export type TabsProps = {
  tabs: TabItem[]
  active: string
  onChange: (key: string) => void
  className?: string
}

export function Tabs({ tabs, active, onChange, className = '' }: TabsProps) {
  // active 不匹配任何 tab 时让首项留在 Tab 序里，保证键盘仍可进入
  const hasSelection = tabs.some((tab) => tab.key === active)

  // WAI-ARIA 标签页模式（自动激活）：←/→ 循环、Home/End 到首末，焦点移动即选中，
  // 与点击的即时切换语义一致；配合 roving tabindex，Tab 键只停选中项一次。
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const tabElements = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]'))
    const focused = (event.target as HTMLElement).closest?.('[role="tab"]')
    const current = focused ? tabElements.indexOf(focused as HTMLElement) : -1
    if (current < 0 || tabElements.length === 0) return
    let next = -1
    if (event.key === 'ArrowRight') next = (current + 1) % tabElements.length
    else if (event.key === 'ArrowLeft') next = (current - 1 + tabElements.length) % tabElements.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = tabElements.length - 1
    if (next < 0 || next === current) return
    event.preventDefault()
    tabElements[next]!.focus()
    onChange(tabs[next]!.key)
  }

  return (
    <div className={`flex items-center gap-1 border-b border-[var(--color-line)] max-md:overflow-x-auto ${className}`.trim()} role="tablist" onKeyDown={onKeyDown}>
      {tabs.map((tab, index) => {
        const selected = tab.key === active
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected || (!hasSelection && index === 0) ? 0 : -1}
            onClick={() => onChange(tab.key)}
            className={`relative -mb-px inline-flex min-h-[var(--control-height)] shrink-0 cursor-pointer select-none items-center gap-1.5 whitespace-nowrap rounded-t-[var(--radius-sm)] border-b-2 px-3 text-sm transition-colors focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-focus)] max-md:min-h-11 ${
              selected
                ? 'border-[var(--color-primary)] font-medium text-[var(--color-primary)]'
                : 'border-transparent text-[var(--color-muted)] hover:text-[var(--color-ink)]'
            }`}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 ? (
              <span
                className={`rounded-[var(--radius-pill)] px-1.5 text-xs leading-4 ${
                  tab.countTone === 'neutral'
                    ? 'bg-[var(--color-soft)] text-[var(--color-muted)]'
                    : 'bg-[var(--color-danger)] text-[var(--color-on-primary)]'
                }`}
              >
                {tab.count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
